import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfigCard from './ConfigCard';
import { useWebshopLanguages } from '../../WebshopLanguagesContext';

const ALL_LANGS = ['nl', 'en', 'fr'];

/**
 * "Custom links" card. Admins add their own links beyond the required pages.
 * Each link has per-language labels (NL/EN/FR) plus a single URL.
 *
 * value: array of { id, url, labels: { nl, en, fr } }
 * onChange(nextArray) — replaces the full list (parent decides ID strategy).
 */
const CustomLinksCard = ({ value = [], onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const list = Array.isArray(value) ? value : [];

    const update = (idx, patch) => {
        const next = list.map((item, i) => i === idx ? { ...item, ...patch } : item);
        onChange && onChange(next);
    };
    const updateLabel = (idx, lang, val) => {
        update(idx, { labels: { ...(list[idx]?.labels || {}), [lang]: val } });
    };
    const addLink = () => {
        onChange && onChange([
            ...list,
            { id: `cl_${Date.now()}`, url: '', labels: { nl: '', en: '', fr: '' }, position: 'FOOTER', headerPosition: 'EXTRA' },
        ]);
    };
    const removeLink = (idx) => {
        onChange && onChange(list.filter((_, i) => i !== idx));
    };

    return (
        <ConfigCard
            anchor="cfg-card-footer-custom-links"
            title={tr('cfg_footer_custom_links_title', 'Custom links')}
            description={tr('cfg_footer_custom_links_desc', 'Add extra links the storefront will render in the footer.')}
            theme={theme}
        >
            <Stack spacing={2}>
                {list.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {tr('cfg_footer_custom_links_empty', 'No custom links yet. Click "Add link" below to create one.')}
                    </Typography>
                )}
                {list.map((item, idx) => (
                    <CustomLinkRow
                        key={item.id ?? idx}
                        item={item}
                        onUrlChange={(v) => update(idx, { url: v })}
                        onLabelChange={(lang, v) => updateLabel(idx, lang, v)}
                        onPositionChange={(v) => update(idx, { position: v })}
                        onHeaderPositionChange={(v) => update(idx, { headerPosition: v })}
                        onRemove={() => removeLink(idx)}
                        tr={tr}
                    />
                ))}
                <Box>
                    <Button size="small" startIcon={<AddIcon />} onClick={addLink} variant="outlined">
                        {tr('cfg_footer_custom_links_add', 'Add link')}
                    </Button>
                </Box>
            </Stack>
        </ConfigCard>
    );
};

const CustomLinkRow = ({ item, onUrlChange, onLabelChange, onPositionChange, onHeaderPositionChange, onRemove, tr }) => {
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(c => adminLanguages.includes(String(c).toUpperCase()));
    const [langTab, setLangTab] = React.useState(0);
    const activeTab = (langTab >= 0 && langTab < LANGS.length) ? langTab : 0;
    const lang = LANGS[activeTab] || ALL_LANGS[0];
    const labelValue = item.labels?.[lang] ?? '';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    size="small"
                    label={tr('cfg_footer_custom_links_url', 'Link URL')}
                    value={item.url || ''}
                    onChange={(e) => onUrlChange(e.target.value)}
                    fullWidth
                    placeholder="https://example.com/page"
                />
                <IconButton size="small" onClick={onRemove} title={tr('cfg_footer_custom_links_remove', 'Remove link')}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Box>
            {/* Placement — mirrors the old admin: position (footer/header/both)
                plus which header bar when the link sits in the header. */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    select size="small"
                    label={tr('cfg_link_position', 'Positie')}
                    value={(item.position || 'FOOTER').toUpperCase()}
                    onChange={(e) => onPositionChange(e.target.value)}
                    sx={{ minWidth: 170 }}
                >
                    <MenuItem value="FOOTER">{tr('cfg_link_position_footer', 'Footer')}</MenuItem>
                    <MenuItem value="HEADER">{tr('cfg_link_position_header', 'Header')}</MenuItem>
                    <MenuItem value="BOTH">{tr('cfg_link_position_both', 'Header & footer')}</MenuItem>
                </TextField>
                {(item.position === 'HEADER' || item.position === 'BOTH') && (
                    <TextField
                        select size="small"
                        label={tr('cfg_dyn_header_position', 'Balk in de header')}
                        value={(item.headerPosition || 'EXTRA').toUpperCase()}
                        onChange={(e) => onHeaderPositionChange(e.target.value)}
                        sx={{ minWidth: 190 }}
                    >
                        <MenuItem value="EXTRA">{tr('cfg_link_bar_extra', 'Bovenbalk (extra)')}</MenuItem>
                        <MenuItem value="FILTER">{tr('cfg_link_bar_filter', 'Menubalk')}</MenuItem>
                    </TextField>
                )}
            </Box>
            <Box>
                <Tabs
                    value={activeTab}
                    onChange={(_e, v) => setLangTab(v)}
                    variant="standard"
                    sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                >
                    {LANGS.map((l) => <Tab key={l} label={l.toUpperCase()} />)}
                </Tabs>
                <TextField
                    size="small"
                    fullWidth
                    label={tr('cfg_footer_custom_links_label', 'Label')}
                    value={labelValue}
                    onChange={(e) => onLabelChange(lang, e.target.value)}
                    sx={{ mt: 1 }}
                />
            </Box>
        </Box>
    );
};

CustomLinksCard.propTypes = {
    value:    PropTypes.array,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default CustomLinksCard;
